import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Pencil, Plus, Trash2 } from 'lucide-react';

import { cn } from '../../lib/cn';
import { colorForClassIndex } from '../../utils/classColors';
import { Button } from '../ui';
import type { BoundingBox, Class as ClassModel } from '../../types/api';

/**
 * ClassesPanel — top section of the right rail. Replaces the old top-right
 * floating ClassPickerOverlay (now removed entirely).
 *
 * Per PIVOTS_2026_05_31.md § 11:
 *   • List classes with color-dot · name · box-count
 *   • Click row → set active class for next box draw
 *   • Hover row → reveal trash; backend returns 409 class_in_use with count
 *     when the class still has boxes, which we surface in a confirm modal
 *   • + Add class button at the bottom
 *   • Collapsible via chevron in the header
 */
interface ClassesPanelProps {
    isDarkMode?: boolean;
    classes: ClassModel[];
    boxes: BoundingBox[];           // for per-class box counts (cheap; we already have them in state)
    activeClassIndex: number;
    /** class_index values whose boxes are currently hidden on the canvas. */
    hiddenClassIndices: Set<number>;
    onSelectActive: (classIndex: number) => void;
    onToggleVisibility: (classIndex: number) => void;
    onAddClick: () => void;
    onDeleteClass: (cls: ClassModel, inUseCount: number) => void;
    /** Open the EditClassModal — same chrome as Add, prefilled with this row. */
    onEditClick: (cls: ClassModel) => void;
}

export default function ClassesPanel({
    isDarkMode = false,
    classes,
    boxes,
    activeClassIndex,
    hiddenClassIndices,
    onSelectActive,
    onToggleVisibility,
    onAddClick,
    onDeleteClass,
    onEditClick,
}: ClassesPanelProps) {
    const { t } = useTranslation('canvas');

    // Counts per class (constant-time lookup keyed on class_index).
    const countsByIndex = useMemo(() => {
        const map = new Map<number, number>();
        for (const b of boxes) map.set(b.class_index, (map.get(b.class_index) ?? 0) + 1);
        return map;
    }, [boxes]);

    const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
    const [hoveredId, setHoveredId] = useState<string | null>(null);

    return (
        <section className={cn('flex flex-col h-full min-h-0', isDarkMode ? 'bg-gray-900' : 'bg-white')}>
            <div className="p-3 space-y-1 flex-1 min-h-0 overflow-y-auto">
                    {classes.length === 0 ? (
                        <p className={cn('text-sm py-4 text-center italic', muted)}>
                            {t('no_classes_hint', { defaultValue: 'No classes yet — click + Add class' })}
                        </p>
                    ) : (
                        <ul className="space-y-0.5">
                            {classes.map((c) => {
                                const active = c.class_index === activeClassIndex;
                                const hidden = hiddenClassIndices.has(c.class_index);
                                const count = countsByIndex.get(c.class_index) ?? 0;
                                const color = c.color || colorForClassIndex(c.class_index);
                                const isHovered = hoveredId === c.id;
                                return (
                                    <li
                                        key={c.id}
                                        onMouseEnter={() => setHoveredId(c.id)}
                                        onMouseLeave={() => setHoveredId(null)}
                                    >
                                        <div
                                            onClick={() => onSelectActive(c.class_index)}
                                            className={cn(
                                                'flex items-center gap-1.5 pl-1 pr-2 py-1.5 rounded-md cursor-pointer text-sm transition-colors',
                                                active
                                                    ? isDarkMode
                                                        ? 'bg-primary-500/15 border-l-2 border-primary-400 text-gray-100 font-semibold'
                                                        : 'bg-primary-50 border-l-2 border-primary-500 text-gray-900 font-semibold'
                                                    : isDarkMode
                                                        ? 'border-l-2 border-transparent text-gray-300 hover:bg-gray-800/50'
                                                        : 'border-l-2 border-transparent text-gray-700 hover:bg-gray-50',
                                                hidden && 'opacity-50',
                                            )}
                                        >
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onToggleVisibility(c.class_index);
                                                }}
                                                title={hidden
                                                    ? t('show_class', { defaultValue: 'Show boxes' })
                                                    : t('hide_class', { defaultValue: 'Hide boxes' })}
                                                className={cn(
                                                    'p-1 rounded shrink-0 transition-colors',
                                                    isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500',
                                                )}
                                            >
                                                {hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                                            </button>
                                            <span
                                                className="h-3 w-3 rounded-full shrink-0"
                                                style={{ backgroundColor: color }}
                                                aria-hidden="true"
                                            />
                                            <span className="flex-1 truncate">{c.name}</span>
                                            <span className={cn('text-[11px] tabular-nums', muted)}>
                                                {count > 0 && t('n_boxes', { count, defaultValue: '{{count}} boxes' })}
                                            </span>
                                            {isHovered && (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); onEditClick(c); }}
                                                        title={t('edit_class', { defaultValue: 'Edit class' })}
                                                        className={cn(
                                                            'p-0.5 rounded transition-colors',
                                                            isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500',
                                                        )}
                                                    >
                                                        <Pencil className="h-3.5 w-3.5" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onDeleteClass(c, count);
                                                        }}
                                                        title={t('delete_class', { defaultValue: 'Delete class' })}
                                                        className={cn(
                                                            'p-0.5 rounded transition-colors',
                                                            isDarkMode ? 'hover:bg-red-900/30 text-red-400' : 'hover:bg-red-50 text-red-500',
                                                        )}
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    <div className="pt-2">
                        <Button
                            variant="ghost"
                            isDarkMode={isDarkMode}
                            size="sm"
                            fullWidth
                            leftIcon={<Plus className="h-3.5 w-3.5" />}
                            onClick={onAddClick}
                        >
                            {t('add_class', { defaultValue: 'Add class' })}
                        </Button>
                    </div>
            </div>
        </section>
    );
}
